import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 80px 0;
  background-color: #f9fafb;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #1f2937;

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const LessonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const LessonCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
  perspective: 1000px;
  cursor: pointer;
  height: 300px;
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${props => props.flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardFront = styled(CardFace)`
  background: white;
`;

const CardBack = styled(CardFace)`
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  transform: rotateY(180deg);
`;

const LessonHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;

  i {
    font-size: 2rem;
    color: #dc2626;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
`;

const LessonDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  text-align: center;
`;

const LessonImpact = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ImpactLabel = styled.span`
  font-weight: 500;
  color: #374151;
`;

const ImpactLevel = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.level === 'high' ? '#fef2f2' : '#fef3c7'};
  color: ${props => props.level === 'high' ? '#dc2626' : '#d97706'};
`;

const SourceInfo = styled.div`
  text-align: center;
  margin-top: 1rem;
`;

const SourceTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const SourceDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
`;

const FlipHint = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: #9ca3af;
  opacity: 0.7;
`;

const LessonsLearned = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [flippedCards, setFlippedCards] = useState({});
  const [timeouts, setTimeouts] = useState({});

  const toggleCard = (index) => {
    // Clear any existing timeout for this card
    if (timeouts[index]) {
      clearTimeout(timeouts[index]);
    }

    // Flip the card
    setFlippedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));

    // Set auto-flip back timer (4 seconds)
    const timeoutId = setTimeout(() => {
      setFlippedCards(prev => ({
        ...prev,
        [index]: false
      }));
      // Clean up timeout reference
      setTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[index];
        return newTimeouts;
      });
    }, 4000);

    // Store timeout reference
    setTimeouts(prev => ({
      ...prev,
      [index]: timeoutId
    }));
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [timeouts]);

  const lessons = [
    {
      icon: 'fas fa-comment-dots',
      title: 'Feedback Collection Gap',
      description: 'Currently collecting feedback from event hosts but not attendees. Need centralized attendee input system with QR codes.',
      impact: 'high',
      source: {
        title: 'October Committee Meeting',
        details: 'Tech Week Engagement Committee Meeting - October 14, 2025',
        feedback: 'QR code surveys discussed as solution for speaker feedback'
      }
    },
    {
      icon: 'fas fa-star-half-alt',
      title: 'Event Quality Standards',
      description: 'Too many events felt sales-focused rather than educational. Events should be 90% educational/informative with minimal direct selling.',
      impact: 'high',
      source: {
        title: 'Committee Discussion',
        details: 'October 14, 2025 Meeting Notes',
        feedback: 'Panel discussions consistently performed well because they featured multiple organizations'
      }
    },
    {
      icon: 'fas fa-building',
      title: 'Venue Capacity Issues',
      description: 'Sold-out keynote left many unable to attend. Need larger venues or overflow solutions for major events.',
      impact: 'high',
      source: {
        title: 'Attendee Feedback',
        details: 'Wozniak keynote sold out 1,300-seat GLC Live @ 20 Monroe',
        feedback: 'Many people unable to attend marquee session'
      }
    },
    {
      icon: 'fas fa-calendar-alt',
      title: 'Scheduling Overlaps',
      description: 'Heat map showed 7 events on Tuesday at 11am versus only 2 events on Thursday at 11am. Need better coordination.',
      impact: 'medium',
      source: {
        title: 'Brandon Hamilton Analysis',
        details: 'Claude AI heat map analysis of event timing',
        feedback: 'Important tech leaders forced to choose between events'
      }
    },
    {
      icon: 'fas fa-bullhorn',
      title: 'Marketing Reach Gap',
      description: 'Tech Week remains "the best conference no one\'s heard of" with limited presence in Black, Brown, and diverse communities.',
      impact: 'high',
      source: {
        title: 'Committee Observation',
        details: 'October 14, 2025 Meeting Discussion',
        feedback: 'Church communities, neighborhood communities, and general public largely unaware'
      }
    },
    {
      icon: 'fas fa-users',
      title: 'Diversity & Inclusion Gap',
      description: 'National Black Administrators conference was in Grand Rapids during Tech Week but wasn\'t connected to programming.',
      impact: 'high',
      source: {
        title: 'Community Partner Feedback',
        details: 'Black Tech Saturdays founder Johnny called looking for Tech Week events',
        feedback: 'GRCC, NAACP, and other Black economic hub organizations were not involved'
      }
    }
  ];

  return (
    <Section id="lessons">
      <Container>
        <SectionTitle
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          Key Lessons from 2025
        </SectionTitle>
        
        <LessonsGrid>
          {lessons.map((lesson, index) => (
            <LessonCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => toggleCard(index)}
            >
              <CardInner flipped={flippedCards[index]}>
                <CardFront>
                  <LessonHeader>
                    <i className={lesson.icon}></i>
                    <h3>{lesson.title}</h3>
                  </LessonHeader>
                  
                  <LessonDescription>{lesson.description}</LessonDescription>
                  
                  <LessonImpact>
                    <ImpactLabel>Impact:</ImpactLabel>
                    <ImpactLevel level={lesson.impact}>
                      {lesson.impact}
                    </ImpactLevel>
                  </LessonImpact>
                  
                  <FlipHint>Click to see source</FlipHint>
                </CardFront>
                
                <CardBack>
                  <SourceInfo>
                    <SourceTitle>{lesson.source.title}</SourceTitle>
                    <SourceDetails>
                      <div style={{ marginBottom: '0.5rem' }}>
                        {lesson.source.details}
                      </div>
                      <div style={{ fontStyle: 'italic', color: '#374151' }}>
                        "{lesson.source.feedback}"
                      </div>
                    </SourceDetails>
                  </SourceInfo>
                  
                  <FlipHint>Auto-flips back in 4s</FlipHint>
                </CardBack>
              </CardInner>
            </LessonCard>
          ))}
        </LessonsGrid>
      </Container>
    </Section>
  );
};

export default LessonsLearned;
